import { date, LocalStorage } from 'quasar'
import { cloneDeep, startCase } from 'lodash-es'
import { formatTimer } from '@/utils/time.util'
import { $flagsmith } from '@/boot/flagsmith'
import { nameToCode } from '@/utils/states.util'

/**
 * Enrich product images with https and sort by media type
 * @param {Object[]} images - Array of images
 * @returns {Object[]} - Array of images
 */
export function enrichProductImages(images = []) {
  const imagePriority = ['KNOCKOUT', 'PRODUCT', 'BOX', 'DAMAGE']

  if (!images) {
    return []
  }

  return (
    images
      .map(image => {
        // force https
        image.url = image.url
          .replace(/(http|https)?:\/\//, 'https://')
          .replace('preprodimages.rushmarket.com', 'preprodrushimages.blob.core.windows.net')

        return image
      })
      // ensure knockout images are first, followed by product images
      .sort((a, b) => {
        const aIndex = imagePriority.indexOf(a.mediaType) + 1 || imagePriority.length
        const bIndex = imagePriority.indexOf(b.mediaType) + 1 || imagePriority.length

        return aIndex - bIndex
      })
  )
}

/**
 * Build product route and full URL, considering partner experience
 * @param {string} productId - The product ID
 * @returns {Object} - Object containing route and full URL
 */
function buildProductRouteUrl(productId) {
  // check for partner experience

  const activePartner = LocalStorage.getItem('peg_partner_exp')
  const route = { name: 'ProductHome', params: { productId } }
  let root = window.location.origin

  // adjust route and URL for valid partner experience if applicable
  if (activePartner && $flagsmith.getValue('partner_exp_whitelist').includes(activePartner)) {
    route.name = 'PartnerProduct'
    route.params.partnerSlug = activePartner
    root = `${root}/p/${activePartner}`
  }

  return { route, url: `${root}/product/${productId}` }
}

/**
 * Get ship-from location string from customer data
 * @param {Object} customerData - Customer data containing city and state
 * @returns {string|null} - Formatted ship-from location or null if data is incomplete
 */
function getShipFromLocation(productData) {
  if (productData.source === 'CUSTOMER_RETURN') {
    const { city, state } = productData?.customerData || {}
    if (city && state) {
      return `${startCase(city.toLowerCase())}, ${nameToCode(state)}`
    }
  }

  return null
}

/**
 * Enrich auction item with additional properties
 * @param {Object} item - Auction item
 * @returns {Object} - Parsed auction item
 */
export function enrichAuctionItem(item) {
  const data = cloneDeep(item)

  // product has already been enriched if endTimer exists
  if (!('endTimer' in data)) {
    data.images = enrichProductImages(data.images)
  }

  // totalExpireTime comes from the cart when a `WON` item exists
  // each item in the cart can have a different purchaseExpireTime and totalExpireTime
  data.secondsToEnd = date.getDateDiff(new Date(data.totalExpireTime || data.purchaseExpireTime || null), new Date(), 'seconds')

  data.secondsToStart = date.getDateDiff(new Date(data.auctionStart), new Date(), 'seconds')

  // start/end timers
  data.startTimer = data.secondsToStart > -1 ? formatTimer(data.secondsToStart) : null
  data.endTimer = data.secondsToStart < 1 && data.secondsToEnd > -1 ? formatTimer(data.secondsToEnd) : null

  // status
  data.pending = data.status === 'PRE-AUCTION' || data.secondsToStart > 0
  data.auction = ['AUCTION', 'UP_FOR_GRABS'].includes(data.status)
  data.sold =
    ['SOLD', 'WON', 'UNPAID', 'CANCELED', 'REFUNDED'].includes(data.status) ||
    (data.auction && data.secondsToEnd <= 0 && data.bidCount > 0)
  data.won = ['WON', 'UNPAID'].includes(data.status)
  data.unpaid = ['UNPAID'].includes(data.status)
  data.expired = ['EXPIRED'].includes(data.status) || (data.auction && !data.sold && data.secondsToEnd <= 0)
  data.relisted = ['RELISTED'].includes(data.status)
  data.liveAuction = ['AUCTION'].includes(data.status) && !data.pending && !data.sold && !data.expired
  data.upForGrabs = ['UP_FOR_GRABS'].includes(data.status) && !data.expired && !data.sold
  data.active = data.liveAuction || data.upForGrabs

  data.minBid = data.minBid || data.startingPrice
  // item can be added to cart only if it's a live auction, has a buy-it-now price greater than min bid, and hasn't expired, been sold, or won
  data.canAddToCart = Boolean(data.liveAuction && data.buyItNowPrice > data.minBid && !data.expired && !data.sold && !data.won)
  data.originalPrice = data[$flagsmith.getValue('original_price_prop')] || data.retailPrice
  // don't mutate purchasePrice - need to always ensure that's coming from the server
  data.auctionPurchasePrice =
    data.purchasePrice || (data.canAddToCart ? data.buyItNowPrice : data.currentBid || data.startingPrice)

  const { delivery, pickup } = data.fulfillmentOptions || {}
  data.deliveryCost = delivery?.cost || 0
  data.shipFromLocation = getShipFromLocation(data)
  data.deliveryEdd = delivery?.enhancedEdd || null
  data.canShip = Boolean(delivery?.eligible && delivery?.cost > 0)
  data.canPickup = Boolean(pickup?.eligible)

  const itemId = data.relistedId || data.auctionItemId || data.itemId || data.id
  const { route, url } = buildProductRouteUrl(itemId)
  data.pdpRoute = route
  data.fullProductUrl = url

  return data
}
